"use client";

import { useEffect, useRef, useState, type ComponentType } from "react";
import { useRouter } from "next/navigation";

import { Spinner } from "@/components/loading";
import { bridge } from "@/lib/bridge";
import { WEB_SURFACE } from "@/generated/surface";
import { useI18n } from "@/lib/i18n/provider";
import { trackFrameInputModality } from "@/lib/input-modality";

// Migration Watch (its heat map and summary) is its own chunk (S#700). It is
// fetched once the map is up, and the sheet opens only with it in hand, so the
// sheet never appears without its back chevron.
type WatchView = ComponentType<{ initialSpecies: string; onClose: () => void }>;
let watchChunk: Promise<WatchView> | null = null;
const loadWatch = () =>
  (watchChunk ??= import("@/features/migration/migration-watch-page").then((m) => m.MigrationWatchView as WatchView));

export function MapPage() {
  const router = useRouter();
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const dockProbeRef = useRef<HTMLDivElement | null>(null);
  const postInsetsRef = useRef<(() => void) | null>(null);
  const [watching, setWatching] = useState<string | null>(null);
  // Until the map document is up (Leaflet and the map code load inside it),
  // the stage shows the ProgressView a fresh iOS map shows.
  const [mapUp, setMapUp] = useState(false);
  const [MigrationWatchView, setMigrationWatchView] = useState<WatchView | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // screens.json#/screens/map/$webBridge: the parent validates event.origin
      // AND event.source, and wildcard postMessage targets are forbidden. The
      // map is a same-origin document under /terra-map, so anything arriving
      // from another origin or another window is not ours.
      if (event.origin !== window.location.origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;

      const data = event.data;
      if (!data || typeof data !== "object") return;

      // The map is listening: send it the chrome clearance (S#697).
      if (data.type === "mapReady") {
        postInsetsRef.current?.();
        setMapUp(true);
        void loadWatch().catch(() => {});
        return;
      }
      // Exactly three navigation messages are allowed on this channel.
      if (data.type === "stationSelected" && typeof data.stationId === "string") {
        // from=map: the station page puts Tags second (screens.json nonOwnedFromWorldMap).
        router.push(`/stations/${encodeURIComponent(data.stationId)}?from=map`);
        return;
      }
      // The focused species' Migration Watch (iOS: a sheet over the map).
      if (data.type === "speciesDetails" && typeof data.commonName === "string" && data.commonName) {
        const species = data.commonName;
        loadWatch()
          .then((view) => {
            setMigrationWatchView(() => view);
            setWatching(species);
          })
          .catch((error) => bridge.log(`map: Migration Watch failed to load: ${String(error)}`, "map"));
        return;
      }
      if (data.type === "tagSelected" && typeof data.tagId === "string") {
        const tech = typeof data.technology === "string" ? `&technology=${encodeURIComponent(data.technology)}` : "";
        router.push(`/tags?tag=${encodeURIComponent(data.tagId)}${tech}`);
        return;
      }
      bridge.log(`map: ignored unrecognised bridge message ${String(data.type)}`, "map");
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [router]);

  // The map runs full bleed under the docked nav and the mini-player, so its
  // own panels (Layers, callouts, the attribution, the bottom-left controls)
  // must stop at the top of that stack. The probe is exactly
  // --dock-stack-height tall — the same clearance every page pads by, the
  // mini-player's measured height included — and its height is posted to the
  // map (screens.json#/screens/map/$webBridge: {type:'chromeInsets',bottom}).
  useEffect(() => {
    const frame = frameRef.current;
    const probe = dockProbeRef.current;
    if (!frame || !probe) return;
    const post = () =>
      frame.contentWindow?.postMessage(
        { type: "chromeInsets", bottom: Math.ceil(probe.getBoundingClientRect().height) },
        window.location.origin,
      );
    postInsetsRef.current = post;
    post();
    const observer = new ResizeObserver(post);
    observer.observe(probe);
    frame.addEventListener("load", post);
    const untrack = trackFrameInputModality(frame);
    return () => {
      observer.disconnect();
      frame.removeEventListener("load", post);
      untrack();
      postInsetsRef.current = null;
    };
  }, []);

  // The Migration Watch sheet: Escape closes it, and focus starts on its back
  // control (the first focusable element of its header).
  useEffect(() => {
    if (!watching) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setWatching(null);
    };
    window.addEventListener("keydown", onKey);
    document.querySelector<HTMLElement>(".tk-sheet-full .tk-back")?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [watching]);

  // The app's Live Map is the whole screen under its navigation chrome: no
  // title, no card around it. The stage fills the viewport; the docked nav and
  // the mini-player float over its bottom edge, and the map document keeps its
  // own panels clear of them (--map-dock-clearance, posted from the probe).
  return (
    <>
      <h1 className="tk-visually-hidden">{t("web.nav.map")}</h1>
      <div ref={dockProbeRef} className="tk-dock-probe" aria-hidden="true" />
      <div className="tk-map-stage">
        <iframe
          ref={frameRef}
          src={`${WEB_SURFACE.basePath}terra-map/index.html`}
          title={t("web.nav.map")}
          allow="geolocation"
          className="tk-map-frame"
        />
        {!mapUp ? (
          <div className="tk-map-pending" role="status" aria-busy="true">
            <Spinner />
            <span className="tk-visually-hidden">{t("map.loadingStations")}</span>
          </div>
        ) : null}
      </div>
      {watching && MigrationWatchView ? (
        <div className="tk-sheet-full" role="dialog" aria-modal="true" aria-label={t("migrationWatch.title")}>
          <MigrationWatchView key={watching} initialSpecies={watching} onClose={() => setWatching(null)} />
        </div>
      ) : null}
    </>
  );
}
